import moment from 'moment';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import toObject from 'dayjs/plugin/toObject';
import DateFormats from '../backbone/utils/personalizer/DateFormats';

export const JQUERY_DATE_FORMATS = {
    'MM/DD/YYYY': 'mm/dd/yy',
    'DD/MM/YYYY': 'dd/mm/yy',
    'YYYY/MM/DD': 'yy/mm/dd'
};

export const getFirstDayOfWeek = (userPreferences) => {
    let firstDayOfWeek = userPreferences.firstDayOfWeek.dayId - 2;

    if (firstDayOfWeek < 0) {
        firstDayOfWeek += 7;
    }

    return firstDayOfWeek;
};

/**
 * Returns the moment for the start of the week based on firstDayofWeek passed in and the date
 *
 * @param firstDayOfWeek: number representing the first day of the week. Sunday = 0, Monday = 1, etc..
 * @param date: js date object, string, or moment representing the date you want to find the start of
 * the week from
 */
export const getFirstDayOfWeekMoment = (firstDayOfWeek = 0, date) => {
    let weekStart = moment(date).day(firstDayOfWeek);

    if (weekStart.isAfter(date, 'day')) {
        weekStart.add(-7, 'day');
    }

    return weekStart;
};

/**
 * This function returns the first day of the week based on the firstDayOfWeek passed in
 * @param firstDayOfWeek: number representing the first day of the week. Sunday = 0, Monday = 1, etc..
 *
 * @returns {Date} - the first day of the week
 */
export const getFirstDayOfWeekDayjs = (firstDayOfWeek = 0, d = new Date()) => {
    dayjs.extend(utc);
    const date = dayjs(d).day(firstDayOfWeek);
    if (date.isAfter(dayjs(d).utc())) {
        return dayjs(d)
            .day(firstDayOfWeek - 7)
            .toDate();
    } else {
        return date.toDate();
    }
};

/**
 * Returns the moment for the end of the week based on firstDayofWeek passed in and the date
 *
 * @param firstDayOfWeek: number representing the first day of the week. Sunday = 0, Monday = 1, etc..
 * @param date: js date object, string, or moment representing the date you want to find the start of
 * the week from
 */
export const getLastDayOfWeekMoment = (firstDayOfWeek = 0, date) => {
    let weekStart = moment(date).day(firstDayOfWeek);

    if (weekStart.isAfter(date, 'day')) {
        weekStart.add(-7, 'day');
    }

    const weekEnd = weekStart.add(6, 'day');
    return weekEnd;
};

/**
 * This function returns the last day of the week based on the firstDayOfWeek passed in
 * @param firstDayOfWeek: number representing the first day of the week. Sunday = 0, Monday = 1, etc..
 *
 * @returns {Date} - the last day of the week
 */
export const getLastDayOfWeekDayjs = (firstDayOfWeek = 0, d = new Date()) => {
    dayjs.extend(utc);
    const date = dayjs(d).day(firstDayOfWeek);
    if (date.isAfter(dayjs(d).utc())) {
        return dayjs(d)
            .day(firstDayOfWeek - 1)
            .toDate();
    } else {
        return date.add(6, 'day').toDate();
    }
};

/**
 * Returns the moment for the start of the month
 *
 * @param date: js date object, string, or moment representing the date you want to find the start of
 * the month from
 */
export const getFirstDayOfMonthMoment = (date) => {
    return moment(date).startOf('month');
};

/**
 * Returns the moment for the end of the month
 *
 * @param date: js date object, string, or moment representing the date you want to find the start of
 * the month from
 */
export const getLastDayOfMonthMoment = (date) => {
    return moment(date).endOf('month');
};

/**
 * Returns the moment for the start of the month
 *
 * @param date: js date object, string, or moment representing the date you want to find the start of
 * the month from
 */
export const getFirstDayOfYearMoment = (date) => {
    return moment(date).startOf('year');
};

/**
 * Returns the moment for the end of the month
 *
 * @param date: js date object, string, or moment representing the date you want to find the start of
 * the month from
 */
export const getLastDayOfYearMoment = (date) => {
    return moment(date).endOf('year');
};

/**
If all days of the week is passed in, return 'Everyday'
If only the weekend days are passed in, return 'Weekends'
If only the weekdays are passed in, return 'Weekdays'
Else return days delimited by comma.

@param format - optional - leave empty for FULL day display or pass in short or abbr.
*/
export const getDisplayOfDays = (days, format, i18next) => {
    let strOfDays = '',
        numOfDays = days && days.length > 0 ? days.length : 0,
        r = {
            SUNDAY: 0,
            MONDAY: 1,
            TUESDAY: 2,
            WEDNESDAY: 3,
            THURSDAY: 4,
            FRIDAY: 5,
            SATURDAY: 6
        };

    function onlyWeekDaysSelected() {
        if (
            numOfDays === 5 &&
            days.indexOf('MONDAY') !== -1 &&
            days.indexOf('TUESDAY') !== -1 &&
            days.indexOf('WEDNESDAY') !== -1 &&
            days.indexOf('THURSDAY') !== -1 &&
            days.indexOf('FRIDAY') !== -1
        ) {
            return true;
        }
        return false;
    }

    function onlyWeekendsSelected() {
        if (numOfDays === 2 && days.indexOf('SATURDAY') !== -1 && days.indexOf('SUNDAY') !== -1) {
            return true;
        }
        return false;
    }

    if (numOfDays === 7) {
        return i18next.t('common:every_day');
    }

    if (onlyWeekDaysSelected()) {
        return i18next.t('common:weekdays');
    }

    if (onlyWeekendsSelected()) {
        return i18next.t('common:weekends');
    }

    for (let i = 0; i < numOfDays; i += 1) {
        if (days[i]) {
            strOfDays += i18next.t('common:day.' + r[days[i]] + (format ? '.' + format.toLowerCase() : ''));
            //do not add comma to first and last items
            if (i + 1 !== numOfDays) {
                strOfDays += ', ';
            }
        }
    }

    return strOfDays;
};

export const getDaysRemaining = (start, end) => {
    let endDate = moment(end),
        currentDate = moment(start);
    return Math.round(moment.duration(endDate.diff(currentDate)).asDays());
};

export const isCurrentDay = (date) => {
    return moment().isSame(moment(date), 'day');
};

export const isCurrentWeek = (date) => {
    const currentWeek = moment().isoWeek(); // Week number of the current week
    const pageDateWeek = moment(date).isoWeek(); // Week number of the current UDS

    return pageDateWeek === currentWeek;
};

export const findClosestDate = (
    dateArray,
    compareDate,
    getDate = (d) => {
        return d;
    }
) => {
    const [closest] = dateArray.sort((a, b) => {
        const [aDate, bDate] = [a, b].map((d) => {
            return Math.abs(getDate(d) - compareDate);
        });
        return aDate - bDate;
    });

    return closest;
};

/**
 * Returns a date format based on the date format key from viewer user preferences
 */
export const getDateFormat = (formatKey) => {
    switch (formatKey) {
        case DateFormats.MMDDYYYY:
            return 'MM/DD/YYYY';
        case DateFormats.DDMMYYYY:
            return 'DD/MM/YYYY';
        case DateFormats.YYYYMMDD:
            return 'YYYY/MM/DD';
        default:
            return 'MM/DD/YYYY';
    }
};

export const getNumberOfDaysBetween = (startDate, endDate, includeEndDate = false) => {
    // One day Time in ms (milliseconds)
    const ONE_DAY = 1000 * 60 * 60 * 24;
    let cloneStart = new Date(startDate.getTime());
    let cloneEnd = new Date(endDate.getTime());
    // set to start of day
    cloneStart.setHours(0, 0, 0, 0);
    cloneEnd.setHours(0, 0, 0, 0);
    let result = Math.round(cloneEnd.getTime() - cloneStart.getTime()) / ONE_DAY + (includeEndDate ? 1 : 0);
    return +result.toFixed(0);
};

export const getFractionalNumberOfDaysBetween = (startDate, endDate) => {
    // One day Time in ms (milliseconds)
    const ONE_DAY = 1000 * 60 * 60 * 24;
    const result = (endDate.getTime() - startDate.getTime()) / ONE_DAY;
    return result;
};

export const getDifferencesBetweenDates = (startDate, endDate, includeEndDate = false) => {
    let numberOfDays = getNumberOfDaysBetween(startDate, endDate, includeEndDate);
    let numberOfYears = numberOfDays >= 365 ? Math.floor(numberOfDays / 365) : 0;
    let numberOfWeeks = 0;
    let dayInWeek = 0;

    if (numberOfYears > 0) {
        numberOfWeeks = Math.round((numberOfDays % 365) / 7);
        dayInWeek = Math.round((numberOfDays % 365) % 7);
    } else {
        numberOfWeeks = Math.floor(numberOfDays / 7);
        dayInWeek = Math.round(numberOfDays - numberOfWeeks * 7);
    }

    return {
        years: numberOfYears,
        weeks: numberOfWeeks,
        totalDays: numberOfDays,
        days: dayInWeek
    };
};

export const getFirstDayOfLastMonth = () => {
    return moment()
        .subtract(1, 'month')
        .startOf('month');
};

export const dateIsValid = (date) => {
    return date instanceof Date;
};

/**
 * returns the current date in this format yyyy-mm-dd
 */
export const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    let month = (today.getMonth() + 1).toString();
    if (month.length < 2) {
        month = `0${month}`;
    }
    let day = today.getDate().toString();
    if (day.length < 2) {
        day = `0${day}`;
    }

    return `${year}-${month}-${day}`;
};

/**
 * format the date passed in to this format yyyy-mm-dd
 */
export const formatDateYYYYMMDD = (date) => {
    return date.toISOString().split('T')[0];
};

/**
 * return date object in UTC time base on input 'YYYY-MM-DD'
 */
export const YYYYMMDDToUTCDateObj = (dateStr) => {
    return new Date(`${dateStr}T00:00:00`);
};

/**
 * return date object base on input 'YYYY-MM-DD' and the browser time zone
 * @param {string} dateStr
 * @returns {Date} - Date object
 *
 * @example
 *     YYYYMMDDToDateObj('2023-09-05') // Tue Sep 05 2023 08:00:00 GMT+0800 (Taipei Standard Time)
 *     YYYYMMDDToDateObj('2023-9-5')   // Tue Sep 05 2023 00:00:00 GMT+0800 (Taipei Standard Time)
 *     Note: with the input of YYYY-MM-DD format, it'll return the object of UTC time!
 */
export const YYYYMMDDToDateObj = (dateStr) => {
    return new Date(dateStr);
};

/**
 * add number of 'days' to a date string in format 'YYYY-MM-DD', returns date in 'YYYY-MM-DD' format as well
 * @param {string} dateStr
 * @param {number} days
 * @returns {string} date string in the format 'YYYY-MM-DD'
 *
 * @example
 *     addDaysToYYYYMMDDStr('2024-03-10', 1) // returns '2024-03-11'
 */
export const addDaysToYYYYMMDDStr = (dateStr, days) => {
    dayjs.extend(utc);
    return formatDateYYYYMMDD(dayjs.utc(dateStr).add(days, 'day')); // e.g. 2023-12-05
};

/**
 * add number of 'months' to a date string in the format 'YYYY-MM-DD', returns date in 'YYYY-MM-DD' format as well
 * @param {string} dateStr
 * @param {number} months
 * @returns {string} date string in the format 'YYYY-MM-DD'
 *
 * @example
 *     addMonthsToYYYYMMDDStr('2023-12-02', 1) // returns '2024-01-02'
 */
export const addMonthsToYYYYMMDDStr = (dateStr, months) => {
    dayjs.extend(utc);
    return formatDateYYYYMMDD(dayjs.utc(dateStr).add(months, 'month')); // e.g. 2024-01-02
};

/**
 * returns the first day of the given YYYY-MM-DD string in YYYY-MM-DD format
 * @param {string} dateStr
 * @returns {string} date string in the format 'YYYY-MM-DD'
 *
 * @example
 *      getFirstDayOfMonthYYYYMMDDStr('2023-02-14') // returns '2023-02-01'
 */
export const getFirstDayOfMonthYYYYMMDDStr = (dateStr) => {
    const date = new Date(dateStr); // e.g. Tue Feb 14 2023 08:00:00 GMT+0800 (Taipei Standard Time)
    date.setDate(1); // e.g. Wed Feb 01 2023 08:00:00 GMT+0800 (Taipei Standard Time)
    return formatDateYYYYMMDD(date); // e.g. 2023-02-01
};

/**
 * returns the last day of the given YYYY-MM-DD string in YYYY-MM-DD format
 * @param {string} dateStr
 * @returns {string} date string in the format 'YYYY-MM-DD'
 *
 * @example
 *      getLastDayOfMonthYYYYMMDDStr('2023-02-14') // returns '2023-02-28'
 */
export const getLastDayOfMonthYYYYMMDDStr = (dateStr) => {
    const date = new Date(dateStr); // e.g. Tue Feb 14 2023 08:00:00 GMT+0800 (Taipei Standard Time)
    const newDate = new Date(Date.UTC(date.getFullYear(), date.getMonth() + 1, 0)); // e.g. Tue Feb 28 2023 08:00:00 GMT+0800 (Taipei Standard Time)
    return formatDateYYYYMMDD(newDate); // e.g. 2023-02-28
};

/**
 * return short month string ex. 'Jan' for given date string in format YYYY-MM-DD
 */
export const getShortMonthStrFromYYYYMMDD = (dateStr, i18next) => {
    const month = dateStr.split('-')[1];
    return i18next.t(`common:month.abbr.${month}`);
};

/**
 * returns the UTC offset for the given timezone
 */
export const getOffset = (timeZone = 'UTC', date = new Date()) => {
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    let tzDate;
    try {
        tzDate = new Date(date.toLocaleString('en-US', { timeZone }));
    } catch (err) {
        // Invalid timeZone
        tzDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    }
    return (tzDate.getTime() - utcDate.getTime()) / 36000;
};

export const addWeeks = (date, weeks) => {
    let newDate = new Date(date.getTime());
    newDate.setDate(newDate.getDate() + 7 * weeks);
    return newDate;
};

export const subtractWeeks = (date, weeks) => {
    let newDate = new Date(date.getTime());
    newDate.setDate(newDate.getDate() - 7 * weeks);
    return newDate;
};

export const subtractDays = (date, days) => {
    let newDate = new Date(date.getTime());
    newDate.setDate(newDate.getDate() - days);
    return newDate;
};

export const addDays = (date, days) => {
    let newDate = new Date(date.getTime());
    newDate.setDate(newDate.getDate() + days);
    return newDate;
};

export const startOfDay = (date) => {
    let newDate = new Date(date.getTime());
    newDate.setHours(0);
    newDate.setMinutes(0);
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);
    return newDate;
};

export const endOfDay = (date) => {
    let newDate = new Date(date.getTime());
    newDate.setHours(23);
    newDate.setMinutes(59);
    newDate.setSeconds(59);
    newDate.setMilliseconds(999);
    return newDate;
};

/**
 * returns the HH:mm UTC offset for the given timezone
 */
export const getOffsetHHmm = (timeZone = 'UTC', date = new Date()) => {
    const timeZoneOffset = getOffset(timeZone, date) / 100.0; // Turn offset into decimal hours (ex. 550 -> 5.5)
    const sign = Math.sign(timeZoneOffset);
    const hours = Math.floor(Math.abs(timeZoneOffset));
    const minutes = (Math.abs(timeZoneOffset) - hours) * 60;
    return {
        hours: hours * sign,
        minutes: minutes * sign
    };
};

/**
 * @param {string} timeZone - the timezone to get the offset for Ex. 'America/Chicago'
 * @param {Date} date - the date to get the offset for Ex. new Date() or new Date('2020-01-01')
 *
 * @returns {number} - the UTC offset for the given timezone and date in minutes Ex. -360
 *
 * @example getUTCOffset('America/Chicago', new Date(2020, 6, 1)) // returns -300 (CDT)
 * @example getUTCOffset('America/Chicago', new Date(2020, 0, 1)) // returns -360 (CST)
 */
export const getUTCOffset = (timeZone, date) => {
    dayjs.extend(timezone);
    dayjs.extend(utc);
    dayjs.extend(toObject);

    return dayjs(date)
        .tz(timeZone, false)
        .utcOffset();
};

/**
 * Gets a date range between two dates
 *
 * @param {Date} startDate the start of the date range
 * @param {Date} endDate the end of the date range
 * @param {number} [dayIntervalScalar] the number of days between each date in the range
 * @returns a list of dates from the start date to the end date
 */
export const getDateRange = (startDate, endDate, dayIntervalScalar = 1) => {
    const startDateWithoutTime = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const endDateWithoutTime = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

    // Count up if the start date is before the end date or count down if the start date is after the end date
    const dayInterval = (startDateWithoutTime <= endDateWithoutTime ? 1 : -1) * Math.abs(dayIntervalScalar);

    /** @type {Date[]} */
    const dateRange = [];

    for (
        let date = new Date(startDateWithoutTime);
        dayInterval >= 0
            ? // If counting up, the date keeps counting until it becomes larger than the end date
              date <= endDateWithoutTime
            : // If counting down, the date keeps counting until it becomes smaller than the end date
              date >= endDateWithoutTime;
        date = new Date(date.getFullYear(), date.getMonth(), date.getDate() + dayInterval)
    ) {
        dateRange.push(date);
    }

    return dateRange;
};

/**
 * returns if the given date is within a given amount of days from today
 * @param {Date} date
 * @param {Number} days
 * @returns {Boolean}
 */

export const isWithinDaysFromToday = (date, days) => {
    const today = new Date();
    const dateToCompare = new Date(date);
    const diff = Math.abs(today.getTime() - dateToCompare.getTime());
    const diffDays = Math.ceil(diff / (1000 * 3600 * 24));
    return diffDays <= days;
};

export const addYears = (date, years) => {
    let newDate = new Date(date.getTime());
    newDate.setFullYear(newDate.getFullYear() + years);
    return newDate;
};

export const weeksBetween = (startDate, endDate, includeEndDate = false) => {
    const daysBetween = getNumberOfDaysBetween(startDate, endDate, includeEndDate);
    return Math.round(daysBetween / 7);
};
